import { apiClient, handleApiSuccess } from './api';
import { Post, CreatePostRequest, PostComment, PostsResponse } from '../types';

/**
 * Post Service
 * Handles all post-related API calls
 */
export class PostService {
  /**
   * Get paginated posts for campus feed
   */
  static async getPosts(page: number = 1, limit: number = 20): Promise<PostsResponse> {
    const response = await apiClient.get(`/posts?page=${page}&limit=${limit}`);
    const data = handleApiSuccess(response);

    return {
      items: data.posts,
      pagination: data.pagination,
    };
  }

  /**
   * Get a specific post by ID
   */
  static async getPost(postId: string): Promise<Post> {
    const response = await apiClient.get(`/posts/${postId}`);
    return handleApiSuccess(response);
  }

  /**
   * Create a new post
   */
  static async createPost(postData: CreatePostRequest): Promise<Post> {
    const response = await apiClient.post('/posts', postData);
    return handleApiSuccess(response);
  }

  /**
   * Update a post (author only)
   */
  static async updatePost(postId: string, postData: Partial<CreatePostRequest>): Promise<Post> {
    const response = await apiClient.put(`/posts/${postId}`, postData);
    return handleApiSuccess(response);
  }

  /**
   * Delete a post (author only)
   */
  static async deletePost(postId: string): Promise<void> {
    const response = await apiClient.delete(`/posts/${postId}`);
    handleApiSuccess(response);
  }

  /**
   * Like or unlike a post
   */
  static async toggleLike(postId: string): Promise<{ isLiked: boolean; likesCount: number }> {
    const response = await apiClient.post(`/posts/${postId}/like`);
    const data = handleApiSuccess(response);

    return {
      isLiked: data.isLiked,
      likesCount: data.totalLikes,
    };
  }

  /**
   * Get comments for a post
   */
  static async getPostComments(postId: string): Promise<PostComment[]> {
    const response = await apiClient.get(`/posts/${postId}/comments`);
    return handleApiSuccess(response);
  }

  /**
   * Add a comment to a post
   */
  static async addComment(postId: string, content: string): Promise<PostComment> {
    const response = await apiClient.post(`/posts/${postId}/comment`, { content });
    return handleApiSuccess(response);
  }

  /**
   * Update a comment (author only)
   */
  static async updateComment(postId: string, commentId: string, content: string): Promise<PostComment> {
    const response = await apiClient.put(`/posts/${postId}/comments/${commentId}`, { content });
    return handleApiSuccess(response);
  }

  /**
   * Delete a comment (author only)
   */
  static async deleteComment(postId: string, commentId: string): Promise<void> {
    const response = await apiClient.delete(`/posts/${postId}/comments/${commentId}`);
    handleApiSuccess(response);
  }

  /**
   * Get user's own posts
   */
  static async getMyPosts(page: number = 1, limit: number = 20): Promise<PostsResponse> {
    const response = await apiClient.get(`/posts/my?page=${page}&limit=${limit}`);
    const data = handleApiSuccess(response);

    return {
      items: data.posts,
      pagination: data.pagination,
    };
  }

  /**
   * Get posts by a specific user
   */
  static async getUserPosts(userId: string, page: number = 1, limit: number = 20): Promise<PostsResponse> {
    const response = await apiClient.get(`/posts/user/${userId}?page=${page}&limit=${limit}`);
    const data = handleApiSuccess(response);

    return {
      items: data.posts,
      pagination: data.pagination,
    };
  }

  /**
   * Search posts
   */
  static async searchPosts(query: string, page: number = 1, limit: number = 20): Promise<PostsResponse> {
    const response = await apiClient.get(`/posts/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`);
    const data = handleApiSuccess(response);

    return {
      items: data.posts,
      pagination: data.pagination,
    };
  }

  /**
   * Upload image for post
   */
  static async uploadImage(file: File, onProgress?: (progress: number) => void): Promise<string> {
    const response = await apiClient.uploadFile('/posts/upload-image', file, onProgress);
    const data = handleApiSuccess(response);
    return data.imageUrl;
  }

  /**
   * Report a post
   */
  static async reportPost(postId: string, reason: string): Promise<void> {
    const response = await apiClient.post(`/posts/${postId}/report`, { reason });
    handleApiSuccess(response);
  }

  /**
   * Get trending posts
   */
  static async getTrendingPosts(limit: number = 10): Promise<Post[]> {
    const response = await apiClient.get(`/posts/trending?limit=${limit}`);
    return handleApiSuccess(response);
  }
}

export default PostService;